'use client';

import { useMemo, useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { IconSearch } from '@tabler/icons-react';
import {
  GPU_PRODUCTS,
  REGION_OPTIONS,
  PRICE_RANGE,
  getAvailableGPUs,
  getComingSoonGPUs,
  getRegionalPrice,
  getRegionName,
  type GPUProduct,
} from '@/lib/data/gpu-products';
import { isInstanceAvailableInRegion, type AirGPURegion } from '@/lib/integrations/airgpu/types';
import { formatHourlyRate } from '@/lib/utils/currency';
import { Button } from '@/components/ui/button';
import { GPUHeroSection } from './gpu-hero-section';
import { GPUFilterBar, type GPUFilters } from './gpu-filter-bar';
import { GPUProductCard, GPUProductCardCompact } from './gpu-product-card';
import { GPUSidebarWidgets, FeaturedGPUCard } from './gpu-sidebar-widgets';
import { machineCreateHref, productsHref } from '@/lib/catalog/filters';

const NO_FILTERS: GPUFilters = {
  gpuType: null,
  region: null,
  priceRange: [PRICE_RANGE.min, PRICE_RANGE.max],
  showComingSoon: true,
};

export function GPUProductsPageClient() {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Parse filters from URL
  const filters: GPUFilters = useMemo(() => {
    const gpuType = searchParams.get('gpu');
    const region = searchParams.get('region') as AirGPURegion | null;
    const priceMin = parseFloat(searchParams.get('priceMin') || String(PRICE_RANGE.min));
    const priceMax = parseFloat(searchParams.get('priceMax') || String(PRICE_RANGE.max));

    return {
      gpuType: gpuType && GPU_PRODUCTS.some((g) => g.id === gpuType) ? gpuType : null,
      region: region && REGION_OPTIONS.some((r) => r.id === region) ? region : null,
      priceRange: [
        isNaN(priceMin) ? PRICE_RANGE.min : Math.max(PRICE_RANGE.min, priceMin),
        isNaN(priceMax) ? PRICE_RANGE.max : Math.min(PRICE_RANGE.max, priceMax),
      ] as [number, number],
      showComingSoon: searchParams.get('showComingSoon') !== 'false',
    };
  }, [searchParams]);

  // Update URL when filters change
  const updateFilters = useCallback(
    (newFilters: GPUFilters) => {
      // Built by the same productsHref() every link on the site uses, so the
      // address bar after filtering and the href that would have produced this
      // view are the same string.
      router.push(
        productsHref({
          gpu: newFilters.gpuType || undefined,
          region: newFilters.region || undefined,
          priceMin:
            newFilters.priceRange[0] !== PRICE_RANGE.min
              ? newFilters.priceRange[0]
              : undefined,
          priceMax:
            newFilters.priceRange[1] !== PRICE_RANGE.max
              ? newFilters.priceRange[1]
              : undefined,
        }),
        { scroll: false },
      );
    },
    [router]
  );

  // Filter products
  const filteredProducts = useMemo(() => {
    return GPU_PRODUCTS.filter((gpu) => {
      if (filters.gpuType && gpu.id !== filters.gpuType) {
        return false;
      }

      // The slider works on base rates (it says so), so a region pick does not
      // move a card in or out of the price window.
      if (gpu.basePrice < filters.priceRange[0] || gpu.basePrice > filters.priceRange[1]) {
        return false;
      }

      // A region filter shows what can actually be created there. Without
      // this the create form would silently swap the region for the first one
      // that offers the GPU.
      if (filters.region) {
        if (!gpu.instanceType || !isInstanceAvailableInRegion(gpu.instanceType, filters.region)) {
          return false;
        }
      }

      if (!filters.showComingSoon && !gpu.available) {
        return false;
      }

      return true;
    });
  }, [filters]);

  // Create-form link for a product, carrying the region filter when set.
  const rentHref = useCallback(
    (gpu: GPUProduct) =>
      gpu.available && gpu.instanceType
        ? machineCreateHref({ instanceType: gpu.instanceType, region: filters.region ?? undefined })
        : undefined,
    [filters.region]
  );

  // Stats
  const stats = useMemo(() => {
    const availableGPUs = getAvailableGPUs();
    return {
      totalGPUs: availableGPUs.length,
      totalRegions: REGION_OPTIONS.length,
      lowestPrice: Math.min(...availableGPUs.map((g) => g.basePrice)),
    };
  }, []);

  // Featured: the popular size large, the best-value size beside it, and a
  // coming-soon spotlight when there is one. There is none today
  // (getComingSoonGPUs() never includes withdrawn products), so that slot
  // goes to the cheapest rentable size instead of an empty box.
  const featured = useMemo(() => {
    const rentable = getAvailableGPUs();
    const lead = rentable.find((g) => g.badge === 'Popular');
    const value = rentable.find((g) => g.badge === 'Best Value');
    const spotlight = getComingSoonGPUs()[0];
    const entry = [...rentable]
      .sort((a, b) => a.basePrice - b.basePrice)
      .find((g) => g !== lead && g !== value);
    const side = [value, spotlight ?? entry].filter((g): g is GPUProduct => !!g);
    return { lead, side };
  }, []);

  const showFeatured =
    !filters.gpuType &&
    !filters.region &&
    filters.priceRange[0] === PRICE_RANGE.min &&
    filters.priceRange[1] === PRICE_RANGE.max;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <GPUHeroSection
        totalGPUs={stats.totalGPUs}
        totalRegions={stats.totalRegions}
        lowestPrice={stats.lowestPrice}
      />

      <GPUFilterBar
        filters={filters}
        onFiltersChange={updateFilters}
        resultCount={filteredProducts.filter((gpu) => gpu.available).length}
      />

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex gap-8">
          <div className="flex-1 min-w-0">
            {/* Featured Section */}
            {showFeatured && featured.lead && (
              <section className="mb-10">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                  Featured GPUs
                </p>
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-12 lg:col-span-7">
                    <FeaturedGPUCard
                      title={featured.lead.name}
                      subtitle={featured.lead.consumerEquivalent}
                      specs={[
                        `${featured.lead.vram}GB VRAM`,
                        `${featured.lead.vcpus} vCPUs`,
                        `${featured.lead.ram}GB RAM`,
                      ]}
                      price={formatHourlyRate(featured.lead.basePrice)}
                      ctaText="Rent now"
                      href={rentHref(featured.lead) ?? machineCreateHref()}
                      className="h-full min-h-[220px]"
                    />
                  </div>
                  <div className="col-span-12 lg:col-span-5 grid grid-rows-2 gap-4">
                    {featured.side.map((gpu) => (
                      <GPUProductCardCompact key={gpu.id} gpu={gpu} rentHref={rentHref(gpu)} />
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* All GPUs Grid */}
            <section>
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {showFeatured
                    ? 'All GPUs'
                    : `${filteredProducts.length} GPU${filteredProducts.length !== 1 ? 's' : ''}`}
                </p>
                <p className="text-xs text-muted-foreground">
                  {filters.region
                    ? `Rates in ${getRegionName(filters.region)}, before upload bandwidth and extra disk.`
                    : 'From-rates: the region and upload bandwidth can raise the hourly rate.'}
                </p>
              </div>

              {filteredProducts.length === 0 ? (
                <div className="text-center py-16 rounded-2xl border border-dashed border-border">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-muted flex items-center justify-center">
                    <IconSearch className="size-6 text-muted-foreground" aria-hidden />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">No GPUs found</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {filters.region
                      ? 'No GPU in this price range is offered in that region.'
                      : 'Try adjusting your filters to see more options.'}
                  </p>
                  <Button variant="outline" size="sm" onClick={() => updateFilters(NO_FILTERS)}>
                    Clear all filters
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {filteredProducts.map((gpu) => (
                    <GPUProductCard
                      key={gpu.id}
                      gpu={gpu}
                      rentHref={rentHref(gpu)}
                      price={filters.region ? getRegionalPrice(gpu, filters.region) : gpu.basePrice}
                      priceLabel={filters.region ? getRegionName(filters.region) : 'From'}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>

          {/* Sidebar */}
          <aside className="hidden lg:block w-72 shrink-0">
            <div className="sticky top-32">
              <GPUSidebarWidgets />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
